import { PublicFundreadyReportCreateBody } from "@boundless/bl-api-client/public/services.zod"
import { z } from "zod"

import type { DefaultValues, FieldPath } from "react-hook-form"

/**
 * Single source of truth for FundReady intake validation.
 *
 * Mirrors FundReadyIntakeRequestSerializer.validate() on the backend (offer and
 * offerEntered must agree) and adds the "must be positive / required once a
 * toggle is on" rules the intake UI enforces but the generated schema does not
 * (its numeric mins are all 0). Keeping every rule here means the per-step
 * Continue gate (form.trigger) and the final submit validate against the same
 * definition — add a required field and the gate picks it up automatically.
 */
const optionalMoney = z.coerce.number().min(0).default(0)

export const fundReadyIntakeSchema = PublicFundreadyReportCreateBody.extend({
  uccBalance: optionalMoney,
  arSecuredDebt: optionalMoney,
  equipmentSecuredDebt: optionalMoney,
  inventorySecuredDebt: optionalMoney,
  realEstateSecuredDebt: optionalMoney,
}).superRefine((data, ctx) => {
  const requirePositive = (
    value: number | undefined,
    path: (string | number)[],
  ) => {
    if (!value || value <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter an amount greater than zero.",
        path,
      })
    }
  }

  requirePositive(data.monthlyRevenue, ["monthlyRevenue"])
  requirePositive(data.askAmount, ["askAmount"])

  if (data.hasAr) requirePositive(data.arAmount, ["arAmount"])
  if (data.hasEquipment)
    requirePositive(data.equipmentAmount, ["equipmentAmount"])
  if (data.hasInventory)
    requirePositive(data.inventoryAmount, ["inventoryAmount"])
  if (data.hasRealEstate)
    requirePositive(data.realEstateAmount, ["realEstateAmount"])

  // The shared UCC balance must be positive once any class is flagged under
  // it (a UCC filing asserts an outstanding claim). Per-class secured debt
  // has no such rule — it's optional and a blank coerces to $0 (see
  // optionalMoney above).
  const anyUnderUcc =
    data.arUnderUcc ||
    data.equipmentUnderUcc ||
    data.inventoryUnderUcc ||
    data.realEstateUnderUcc
  if (anyUnderUcc) requirePositive(data.uccBalance, ["uccBalance"])

  if (data.offerEntered && !data.offer) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required when offer is entered.",
      path: ["offer"],
    })
  }
  if (!data.offerEntered && data.offer) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Must be true when offer is provided.",
      path: ["offerEntered"],
    })
  }
  if (data.offerEntered && data.offer) {
    requirePositive(data.offer.advance, ["offer", "advance"])
    requirePositive(data.offer.payback, ["offer", "payback"])
    requirePositive(data.offer.termMonths, ["offer", "termMonths"])
  }
})

export type FundReadyIntakeFormValues = z.infer<typeof fundReadyIntakeSchema>

/**
 * A blank numeric input holds the "" sentinel (see EMPTY_AMOUNT), which Zod
 * reports as 'Expected number, received string' — compiler-speak for "this
 * field is empty". Translate just that case; everything else (untouched
 * chips/selects are undefined) keeps Zod's default "Required".
 */
export const fundReadyIntakeErrorMap: z.ZodErrorMap = (issue, ctx) => {
  if (
    issue.code === z.ZodIssueCode.invalid_type &&
    issue.expected === "number" &&
    issue.received === "string"
  ) {
    return { message: "Enter an amount." }
  }
  return { message: ctx.defaultError }
}

/**
 * FormInputNumeric normalizes a cleared input to "" and renders blank for it.
 * We reuse that sentinel when programmatically blanking a dependent amount (an
 * asset toggled on, a debt position added) so the field shows its placeholder
 * instead of a stale 0. Isolated here so the one unavoidable cast doesn't leak
 * across call sites.
 */
export const EMPTY_AMOUNT = "" as unknown as number

/**
 * RHF numeric/select inputs render blank until the user supplies a value, so we
 * only seed fields that have a genuine starting value. The rest stay undefined
 * (DefaultValues is a deep-partial) rather than being cast to fake numbers —
 * FormInputNumeric owns the empty-vs-number seam at the input layer, which
 * keeps the schema types above honest.
 */
export const fundReadyIntakeDefaults: DefaultValues<FundReadyIntakeFormValues> =
  {
    revenueTrend: "flat",
    positions: 0,
    monthlyDebt: 0,
    recurringRevenue: "none",
    hasAr: false,
    arAmount: 0,
    hasEquipment: false,
    equipmentAmount: 0,
    hasInventory: false,
    inventoryAmount: 0,
    hasRealEstate: false,
    realEstateAmount: 0,
    arUnderUcc: false,
    equipmentUnderUcc: false,
    inventoryUnderUcc: false,
    realEstateUnderUcc: false,
    // Blank so the shared UCC-balance box shows its placeholder, not a stale 0,
    // once a class is flagged; a blank coerces to $0 at submit (optionalMoney).
    uccBalance: EMPTY_AMOUNT,
    arSecuredDebt: 0,
    equipmentSecuredDebt: 0,
    inventorySecuredDebt: 0,
    realEstateSecuredDebt: 0,
    offerEntered: false,
    offer: null,
  }

export const EMPTY_OFFER: NonNullable<FundReadyIntakeFormValues["offer"]> = {
  advance: EMPTY_AMOUNT,
  payback: EMPTY_AMOUNT,
  termMonths: EMPTY_AMOUNT,
  frequency: "daily",
  fees: EMPTY_AMOUNT,
}

/**
 * Fields each step owns, in flow order. The Continue gate validates
 * form.trigger(STEP_FIELDS[stepIndex]) so a step blocks on exactly its fields,
 * derived from the schema above.
 */
export const STEP_FIELDS: FieldPath<FundReadyIntakeFormValues>[][] = [
  ["industry", "monthsInBusiness", "monthlyRevenue", "revenueTrend"],
  ["askAmount", "useOfFunds", "urgency"],
  [
    "hasAr",
    "arAmount",
    "arUnderUcc",
    "arSecuredDebt",
    "hasEquipment",
    "equipmentAmount",
    "equipmentUnderUcc",
    "equipmentSecuredDebt",
    "hasInventory",
    "inventoryAmount",
    "inventoryUnderUcc",
    "inventorySecuredDebt",
    "hasRealEstate",
    "realEstateAmount",
    "realEstateUnderUcc",
    "realEstateSecuredDebt",
    "uccBalance",
    "recurringRevenue",
  ],
  ["positions", "monthlyDebt"],
  ["offerEntered", "offer"],
]
